import { ClientSociety } from "@dune/types";
import { ControlledInput } from "@dune/ui";
import { isAxiosError } from "axios";
import { useEffect, useRef } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import { useGetSocietyInfoFromSirenNumber } from "../../../api/queries/siren.queries";
import { updateFormSirenSociety } from "../../../helpers/newClientForm.helper";
import {
	GFAShareholderFormData,
	GFAShareholdersAndExperiencesFormData,
} from "../../gfa/useGFAShareholdersAndExperiencesForm";
import { ConstructionSiteDetailsFormData } from "./useConstructionSiteDetailsForm";
import { CreateConstructionSiteClientFormData } from "./useCreateConstructionSiteForm";

const SirenSocietyForm = ({
	isClient = true,
	formKey,
	onExistingSocietyDetected,
	allowManualAddress = false,
}: {
	isClient?: boolean;
	formKey:
		| "clientSociety"
		| "shareholderSociety"
		| "realEstateSociety"
		| "syndicSociety";
	onExistingSocietyDetected?: (society: ClientSociety) => void;
	/** When true (e.g. syndic), denomination and address fields stay editable so user can enter a society without SIREN */
	allowManualAddress?: boolean;
}) => {
	const { control, formState, setValue, clearErrors } = useFormContext<
		| ConstructionSiteDetailsFormData
		| CreateConstructionSiteClientFormData
		| GFAShareholderFormData
		| GFAShareholdersAndExperiencesFormData
	>();
	const { errors } = formState;
	const societySirenValue = useWatch({
		control,
		name: `${formKey}.siren`,
	});

	const {
		data: societyData,
		isSuccess: isSocietySirenSuccess,
		isError: isSocietySirenError,
		error: societySirenError,
	} = useGetSocietyInfoFromSirenNumber(String(societySirenValue), isClient);
	const previousSocietyId = useRef<number | null>(null);

	const societySirenAxiosError =
		isSocietySirenError && isAxiosError(societySirenError)
			? societySirenError.response?.data
			: null;

	useEffect(() => {
		updateFormSirenSociety(
			isSocietySirenSuccess,
			String(societySirenValue),
			societyData,
			setValue,
			formKey,
			formKey === "syndicSociety",
		);
		if (isSocietySirenSuccess && societyData && "id" in societyData) {
			if (previousSocietyId.current !== societyData.id) {
				onExistingSocietyDetected?.(societyData);
				previousSocietyId.current = societyData.id;
			}
		} else if (!isSocietySirenSuccess) {
			previousSocietyId.current = null;
		}
		clearErrors([formKey]);
	}, [
		societyData,
		isSocietySirenSuccess,
		setValue,
		clearErrors,
		societySirenValue,
		formKey,
		onExistingSocietyDetected,
	]);
	return (
		<>
			<ControlledInput
				control={control}
				name={`${formKey}.siren`}
				label="SIREN/SIRET"
				maxLength={14}
				error={
					societySirenAxiosError
						? societySirenAxiosError.message
						: errors[formKey as keyof typeof errors]?.siren?.message?.toString()
				}
			/>
			<ControlledInput
				control={control}
				name={`${formKey}.denomination`}
				label="Raison sociale"
				error={errors[
					formKey as keyof typeof errors
				]?.denomination?.message?.toString()}
				disabled={
					allowManualAddress
						? false
						: (!!societyData && societyData.denomination) ||
							(!!societySirenAxiosError &&
								societySirenAxiosError?.statusCode !== 500)
				}
			/>

			{formKey !== "realEstateSociety" && (
				<>
					<div className="flex flex-row gap-4 w-full">
						<ControlledInput
							control={control}
							name={`${formKey}.streetNumber`}
							label="Numéro de rue"
							error={errors[
								formKey as keyof typeof errors
							]?.streetNumber?.message?.toString()}
							disabled={
								allowManualAddress
									? false
									: (!!societyData &&
											societyData.headOfficeAddress?.streetNumber) ||
										(!!societySirenAxiosError &&
											societySirenAxiosError?.statusCode !== 500)
							}
						/>
						<ControlledInput
							control={control}
							name={`${formKey}.streetName`}
							label="Nom de rue"
							error={errors[
								formKey as keyof typeof errors
							]?.streetName?.message?.toString()}
							disabled={
								allowManualAddress
									? false
									: (!!societyData &&
											societyData.headOfficeAddress?.streetName) ||
										(!!societySirenAxiosError &&
											societySirenAxiosError?.statusCode !== 500)
							}
						/>
					</div>

					<div className="flex flex-row gap-4 w-full">
						<ControlledInput
							control={control}
							name={`${formKey}.postalCode`}
							label="Code postal"
							error={errors[
								formKey as keyof typeof errors
							]?.postalCode?.message?.toString()}
							disabled={
								allowManualAddress
									? false
									: (!!societyData &&
											societyData.headOfficeAddress?.postalCode) ||
										(!!societySirenAxiosError &&
											societySirenAxiosError?.statusCode !== 500)
							}
						/>
						<ControlledInput
							control={control}
							name={`${formKey}.city`}
							label="Ville"
							error={errors[
								formKey as keyof typeof errors
							]?.city?.message?.toString()}
							disabled={
								allowManualAddress
									? false
									: (!!societyData && societyData.headOfficeAddress?.city) ||
										(!!societySirenAxiosError &&
											societySirenAxiosError?.statusCode !== 500)
							}
						/>
					</div>
				</>
			)}
		</>
	);
};

export default SirenSocietyForm;
